import React, { useState } from 'react';
import { regions } from '../../data/regions';
import RegionStateSelector from './RegionStateSelector';
import { countryData } from '../../data/countryData.ts';

interface RegionSelectorProps {
  selectedRegions: string[];
  onRegionsChange: (regions: string[]) => void;
  additionalDetails?: string;
  onDetailsChange?: (details: string) => void;
}

export default function RegionSelector({ 
  selectedRegions, 
  onRegionsChange,
  additionalDetails = '',
  onDetailsChange 
}: RegionSelectorProps) {
  const [selectedCountries, setSelectedCountries] = useState<Record<string, string[]>>({
    europe: [],
    americas: [],
    africa: [],
    asia: [],
    oceania: []
  });
  
  const handleRegionChange = (regionId: string, checked: boolean) => {
    if (checked) {
      const newRegions = [...selectedRegions, regionId];
      onRegionsChange(newRegions);
    } else {
      const newRegions = selectedRegions.filter(r => r !== regionId);
      onRegionsChange(newRegions);
      // Clear selected states when region is deselected
      setSelectedCountries(prev => ({
        ...prev,
        [regionId]: []
      }));
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 className="text-xl font-semibold mb-4">Select Applicable Regions</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {regions.map((region) => (
          <label key={region.id} className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
              checked={selectedRegions.includes(region.id)}
              onChange={(e) => handleRegionChange(region.id, e.target.checked)}
            />
            <span className="text-sm text-gray-700">{region.name}</span>
          </label>
        ))}
      </div>

      {selectedRegions.map(regionId => (
        <RegionStateSelector
          key={regionId}
          title={`Select ${regions.find(r => r.id === regionId)?.name} Countries`}
          states={countryData[regionId]}
          selectedStates={selectedCountries[regionId]}
          onStatesChange={(states) => setSelectedCountries(prev => ({
            ...prev,
            [regionId]: states
          }))}
        />
      ))}

      {/* Additional Details Field */}
      <div className="mt-6 border-t border-gray-100 pt-6">
        <label htmlFor="additionalDetails" className="block text-sm font-medium text-gray-700 mb-2">
          Business Activity Description
        </label>
        <textarea
          id="additionalDetails"
          name="additionalDetails"
          rows={4}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm"
          placeholder="Please provide a brief description of your business activity, including your industry and core operations."
          value={additionalDetails}
          onChange={(e) => onDetailsChange?.(e.target.value)}
        />
      </div>
    </div>
  );
}